import React from 'react';
import {
  type Control,
  type UseFormGetValues,
  type UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import type { TAutocompleteOption } from '~/components/AutocompleteField/types.ts';
import { useCountryByIdQuery, useCountrySearchQuery } from '~/queries/useCountryQuery.ts';
import type { TGuaranteesBeneficiary } from '~/store/guarantees/types.ts';
import { NON_RESIDENT_BENEFICIARY_IDENTIFY_CODE } from '../../../rootSection/constants.ts';
import { GuaranteesBeneficiaryFieldName } from '../../../rootSection/types.ts';

type TUseBeneficiarySectionFieldsParams = {
  beneficiary: TGuaranteesBeneficiary | null;
  control: Control<TGuaranteesBeneficiary>;
  defaultValues: TGuaranteesBeneficiary;
  getValues: UseFormGetValues<TGuaranteesBeneficiary>;
  setValue: UseFormSetValue<TGuaranteesBeneficiary>;
};

export const useBeneficiarySectionFields = ({
  beneficiary,
  control,
  defaultValues,
  getValues,
  setValue,
}: TUseBeneficiarySectionFieldsParams) => {
  const isNonResidentBeneficiary = useWatch({
    control,
    name: GuaranteesBeneficiaryFieldName.IsNonResidentBeneficiary,
  });
  const selectedResidenceCountry = useWatch({
    control,
    name: GuaranteesBeneficiaryFieldName.BeneficiaryResidenceCountry,
  });
  const hasSavedCountryName = React.useMemo(
    () => !!String(beneficiary?.countryName ?? '').trim(),
    [beneficiary?.countryName]
  );
  const savedCountryId = React.useMemo(
    () =>
      hasSavedCountryName ? '' : String(beneficiary?.countryId ?? '').trim(),
    [beneficiary?.countryId, hasSavedCountryName]
  );
  const [countryInputValue, setCountryInputValue] = React.useState(
    defaultValues.beneficiaryResidenceCountry?.label ?? ''
  );
  const [debouncedCountryInputValue, setDebouncedCountryInputValue] = React.useState(
    defaultValues.beneficiaryResidenceCountry?.label ?? ''
  );
  const previousIdentifyCodeRef = React.useRef(
    defaultValues.identifyCode !== NON_RESIDENT_BENEFICIARY_IDENTIFY_CODE
      ? defaultValues.identifyCode
      : ''
  );
  const { countryOptions, isLoading: isCountrySearchLoading } = useCountrySearchQuery(
    debouncedCountryInputValue
  );
  const { countryOption: savedCountryOption, isLoading: isSavedCountryLoading } =
    useCountryByIdQuery(savedCountryId);
  const residenceCountryOptions: TAutocompleteOption[] = React.useMemo(() => {
    if (!selectedResidenceCountry?.value) {
      return countryOptions;
    }

    return [
      selectedResidenceCountry,
      ...countryOptions.filter(
        (option) =>
          option.value !== selectedResidenceCountry.value &&
          option.label !== selectedResidenceCountry.label
      ),
    ];
  }, [countryOptions, selectedResidenceCountry]);

  React.useEffect(() => {
    const timeoutId = window.setTimeout(
      () => setDebouncedCountryInputValue(countryInputValue),
      300
    );

    return () => window.clearTimeout(timeoutId);
  }, [countryInputValue]);

  React.useEffect(() => {
    const defaultCountryLabel = defaultValues.beneficiaryResidenceCountry?.label ?? '';

    setCountryInputValue(defaultCountryLabel);
    setDebouncedCountryInputValue(defaultCountryLabel);
  }, [defaultValues.beneficiaryResidenceCountry?.label]);

  React.useEffect(() => {
    if (!savedCountryOption) {
      return;
    }

    const currentCountry = getValues(GuaranteesBeneficiaryFieldName.BeneficiaryResidenceCountry);

    if (!currentCountry?.value || currentCountry.value === savedCountryOption.value) {
      setValue(
        GuaranteesBeneficiaryFieldName.BeneficiaryResidenceCountry,
        savedCountryOption,
        { shouldDirty: false, shouldValidate: false }
      );
      setCountryInputValue(savedCountryOption.label);
      setDebouncedCountryInputValue(savedCountryOption.label);
    }
  }, [getValues, savedCountryOption, setValue]);

  React.useEffect(() => {
    if (defaultValues.identifyCode !== NON_RESIDENT_BENEFICIARY_IDENTIFY_CODE) {
      previousIdentifyCodeRef.current = defaultValues.identifyCode;
    }
  }, [defaultValues]);

  const handleNonResidentChange = React.useCallback(
    (isChecked: boolean) => {
      const currentIdentifyCode = getValues(GuaranteesBeneficiaryFieldName.BeneficiaryIdentifyCode);

      if (isChecked) {
        if (
          currentIdentifyCode &&
          currentIdentifyCode !== NON_RESIDENT_BENEFICIARY_IDENTIFY_CODE
        ) {
          previousIdentifyCodeRef.current = currentIdentifyCode;
        }

        setValue(
          GuaranteesBeneficiaryFieldName.BeneficiaryIdentifyCode,
          NON_RESIDENT_BENEFICIARY_IDENTIFY_CODE,
          { shouldDirty: true, shouldValidate: true }
        );
        return;
      }

      setValue(
        GuaranteesBeneficiaryFieldName.BeneficiaryIdentifyCode,
        previousIdentifyCodeRef.current,
        { shouldDirty: true, shouldValidate: true }
      );
    },
    [getValues, setValue]
  );

  return {
    countryInputValue,
    handleNonResidentChange,
    isNonResidentBeneficiary,
    isResidenceCountryLoading: isCountrySearchLoading || isSavedCountryLoading,
    residenceCountryOptions,
    setCountryInputValue,
  };
};
